/**
 * Typed wrappers over the Tauri commands. Nothing else calls `invoke`
 * directly, so a rename on the Rust side breaks exactly one place.
 */
import { invoke } from '@tauri-apps/api/core';

import type {
  AppInfo,
  Capabilities,
  Change,
  Commit,
  CompanionExportOptions,
  CompanionImportReport,
  DeviceEntry,
  Entry,
  LppSummary,
  Note,
  Platform,
  Settings,
  Show,
  Summary,
  SyncReport,
  Tokens,
} from '../types';

export interface ImportResult {
  show: Show;
  summary: Summary;
  kind: string;
}

export interface DeviceRef {
  platform: Platform;
  host: string;
  awjHost?: string;
}

export const toRef = (d: DeviceEntry): DeviceRef => ({ platform: d.platform, host: d.host, awjHost: d.awjHost });

const tauriApi = {
  appInfo: () => invoke<AppInfo>('app_info'),
  settingsGet: () => invoke<Settings>('settings_get'),
  settingsSet: (settings: Settings) => invoke<Settings>('settings_set', { settings }),

  libraryList: () => invoke<Entry[]>('library_list'),
  showLoad: (id: string) => invoke<Show>('show_load', { id }),
  showSave: (show: Show, message: string) => invoke<{ show: Show; commit: Commit | null }>('show_save', { show, message }),
  showNew: (name: string, platform: Platform, model: string) => invoke<Show>('show_new', { name, platform, model }),
  showHistory: (id: string) => invoke<Commit[]>('show_history', { id }),
  showSnapshot: (id: string, commit: string) => invoke<Show>('show_snapshot', { id, commit }),
  showDiff: (id: string, from: string, to: string) => invoke<Change[]>('show_diff', { id, from, to }),
  showRestore: (id: string, commit: string) => invoke<Commit>('show_restore', { id, commit }),
  showDelete: (id: string) => invoke<void>('show_delete', { id }),
  showDuplicate: (id: string, name: string) => invoke<Show>('show_duplicate', { id, name }),
  showValidate: (show: Show) => invoke<string[]>('show_validate', { show }),

  importPath: (path: string) => invoke<ImportResult>('import_path', { path }),
  exportShowJson: (id: string, path: string) => invoke<void>('export_show_json', { id, path }),
  vendorExport: (id: string, sha256: string, path: string) => invoke<void>('vendor_export', { id, sha256, path }),

  bundleExport: (id: string, path: string) => invoke<{ path: string; size: number }>('bundle_export', { id, path }),
  bundleExportAwc: (id: string, sha256: string, path: string) =>
    invoke<{ path: string; size: number; entries: string[]; note: string }>('bundle_export_awc', { id, sha256, path }),
  lppSummary: (id: string) => invoke<LppSummary | null>('lpp_summary', { id }),
  lppAttach: (id: string, path: string) => invoke<LppSummary>('lpp_attach', { id, path }),
  lppExport: (id: string, path: string) => invoke<void>('lpp_export', { id, path }),
  lppDetach: (id: string) => invoke<void>('lpp_detach', { id }),
  writeFile: (path: string, base64: string) => invoke<void>('write_file', { path, base64 }),
  writeText: (path: string, text: string) => invoke<void>('write_text', { path, text }),
  /** The end of a batch written into a folder. Nothing to do on disk; the browser build zips it up. */
  finishFolder: async (_folder: string) => {},
  readText: (path: string) => invoke<string>('read_text', { path }),

  deviceProbe: (dev: DeviceRef) => invoke<Record<string, unknown>>('device_probe', { dev }),
  devicePull: (dev: DeviceRef, opts: { vendorFile: boolean; deepCapture: boolean; intoShow?: string; name?: string }) =>
    invoke<ImportResult>('device_pull', { dev, opts }),
  devicePush: (dev: DeviceRef, id: string, opts: { vendorFile: boolean; modules: string[]; labels: boolean }) =>
    invoke<{ ok: boolean; log: string[] }>('device_push', { dev, id, opts }),
  deviceRecall: (dev: DeviceRef, req: { kind: string; number: number; screen?: string; program: boolean }) =>
    invoke<void>('device_recall', { dev, req }),
  deviceTake: (dev: DeviceRef, screens: string[]) => invoke<void>('device_take', { dev, screens }),

  convertShow: (id: string, target: Platform, model: string, save: boolean) =>
    invoke<{ show: Show; notes: Note[]; saved: boolean }>('convert_show', { id, target, model, save }),
  capabilities: (platform: Platform, model: string) => invoke<Capabilities>('capabilities', { platform, model }),

  companionExport: (id: string, opts: CompanionExportOptions, path: string) =>
    invoke<{ path: string; type: string; pages: number }>('companion_export', { id, opts, path }),
  companionImport: (id: string, path: string) => invoke<CompanionImportReport>('companion_import', { id, path }),

  syncRun: (direction: 'both' | 'push' | 'pull') => invoke<SyncReport>('sync_run', { direction }),
  oauthBegin: (provider: string, clientId: string, clientSecret?: string) =>
    invoke<string>('oauth_begin', { provider, clientId, clientSecret: clientSecret || null }),
  oauthFinish: () => invoke<Tokens>('oauth_finish'),
  oauthRefresh: () => invoke<Tokens>('oauth_refresh'),
};

/** Where the full desktop app is: the project page carries the downloads. */
export const FULL_APP_URL = 'https://stoatworks-labs.com/software/showbook/';

/** True inside the Tauri webview; false in a plain browser tab. */
export const inTauri = '__TAURI_INTERNALS__' in window;

/** True in showbook-lite, the hosted build: the Rust core as WebAssembly, the library in IndexedDB. */
export const isLite = !inTauri && __LITE__;

/** The real commands inside the app; the wasm core in showbook-lite; the in-memory demo in any other tab. */
export const api: typeof tauriApi = inTauri ? tauriApi : isLite ? (await import('./lite')).liteApi : (await import('./mock')).mockApi;
